import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Package, 
  MapPin, 
  User, 
  Calendar,
  DollarSign,
  Scale,
  TrendingUp,
  TrendingDown,
  Truck,
  Clock
} from 'lucide-react';
import type { MerchandiseMovement } from '@/types/merchandise-movement.types';
import { 
  getMovementTypeLabel, 
  getMovementTypeColor, 
  getStatusLabel, 
  getStatusColor,
  getDirectionLabel,
  getDirectionColor
} from '@/types/merchandise-movement.types';

interface MerchandiseMovementApprovalModalProps {
  movement: MerchandiseMovement | null;
  isOpen: boolean;
  onClose: () => void;
  onApproval: (reason: string) => void;
  title?: string;
  actionText?: string;
  reasonLabel?: string;
  reasonPlaceholder?: string;
}

const formatCurrency = (amount: number): string => {
  return `S/ ${amount.toLocaleString('es-PE', { minimumFractionDigits: 2 })}`;
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const MerchandiseMovementApprovalModal: React.FC<MerchandiseMovementApprovalModalProps> = ({
  movement,
  isOpen,
  onClose,
  onApproval,
  title,
  actionText = 'Confirmar Rechazo',
  reasonLabel = 'Especifique la razón del rechazo',
  reasonPlaceholder
}) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionForm, setShowRejectionForm] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setRejectionReason('');
      setShowRejectionForm(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!movement) return null;

  const handleReject = async () => {
    setIsSubmitting(true);
    try {
      await onApproval(rejectionReason || 'Sin razón especificada');
      onClose();
    } catch (error) {
      console.error('Error rejecting movement:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getModalIcon = () => {
    return movement.direction === 'entry' ? TrendingUp : TrendingDown;
  };

  const getModalTitle = () => {
    return title || `Rechazar ${movement.direction === 'entry' ? 'Ingreso' : 'Salida'} de Mercadería`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            {React.createElement(getModalIcon(), { 
              className: `w-5 h-5 mr-2 ${movement.direction === 'entry' ? 'text-green-600' : 'text-red-600'}` 
            })}
            {getModalTitle()}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Movement Details Card */}
          <div className="border rounded-lg p-6 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Product Information */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-gray-900">Información del Producto</span>
                </div>

                <div className="space-y-2 pl-7">
                  <div>
                    <span className="text-sm text-gray-600">Producto:</span>
                    <span className="ml-2 font-medium">{movement.ingredientName}</span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Cantidad:</span>
                    <span className="ml-2 font-medium">
                      {movement.quantity.toLocaleString('es-PE')} {movement.unit}
                    </span>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-gray-900">Ubicación</span>
                </div>

                <div className="space-y-2 pl-7">
                  <div>
                    <span className="text-sm text-gray-600">Ubicación:</span>
                    <span className="ml-2 font-medium">{movement.locationName}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          
          <Button
            onClick={handleReject}
            disabled={isSubmitting}
            className="bg-red-600 hover:bg-red-700"
          >
            <XCircle className="w-4 h-4 mr-2" />
            {isSubmitting ? (title?.includes('Cancelar') ? 'Cancelando...' : 'Rechazando...') : actionText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};